import React, { memo, useState, useMemo, useCallback } from "react";
import { PurpleButton } from "../../../../../Components/Buttons/PurpleButton";
import { WhiteButton } from "../../../../../Components/Buttons/WhiteButton";
import { AmenityIcons } from "../../../../../Components/Rent/AmenityIcons";
import AmenitiesIcon from "../../../../../assets/images/Dashboard/Dashboard/Amenities";
import { CheckMarkPurple, PlusIcon } from "../../../../../AssetComponents/Images";
import { AMENITY_GROUPS, AMENITY_CATEGORIES } from "../constants/amenitiesConfig";
import { countSelectedAmenities, getAmenityLabel } from "../utils/amenityUtils";

const MintAmenitiesPage = memo(({ metaDetails, setMetaDetails, onBack, onNext }) => {
  const [currentCategory, setCurrentCategory] = useState("Bathroom");
  const [search, setSearch] = useState("");

  const selectedAmenities = metaDetails.amenities || {};
  const totalSelected = countSelectedAmenities(selectedAmenities);

  const normalizedGroups = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return AMENITY_GROUPS;

    return AMENITY_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        getAmenityLabel(item).toLowerCase().includes(trimmed)
      ),
    })).filter((group) => group.items.length > 0);
  }, [search]);

  const shouldRenderGroup = useCallback(
    (groupKey) => currentCategory === "All" || search || groupKey === currentCategory,
    [currentCategory, search]
  );

  const toggleAmenity = useCallback(
    (categoryKey, amenityKey) => {
      setMetaDetails((prev) => {
        const previousAmenities = prev.amenities || {};
        const currentList = previousAmenities[categoryKey] || [];
        const exists = currentList.includes(amenityKey);

        const updatedList = exists
          ? currentList.filter((item) => item !== amenityKey)
          : [...currentList, amenityKey];

        const nextAmenities = { ...previousAmenities };

        if (updatedList.length > 0) {
          nextAmenities[categoryKey] = updatedList;
        } else {
          delete nextAmenities[categoryKey];
        }

        return {
          ...prev,
          amenities: nextAmenities,
        };
      });
    },
    [setMetaDetails]
  );

  const isSelected = useCallback(
    (categoryKey, amenityKey) =>
      Array.isArray(selectedAmenities[categoryKey]) &&
      selectedAmenities[categoryKey].includes(amenityKey),
    [selectedAmenities]
  );

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 bg-white border-[2px] border-[#E3E3E3] rounded-[10px] p-[20px] space-y-[20px] overflow-hidden">
        <div className="space-y-[6px]">
          <div className="flex items-center gap-[10px]">
            <AmenitiesIcon />
            <div className="text-[20px] font-semibold">Add amenities</div>
          </div>
          <div className="text-[#595959] max-w-[640px]">
            Select every amenity you offer 
          </div>
          <div className="text-sm text-[#999999]">
            {totalSelected === 0
              ? "No amenities selected yet."
              : `${totalSelected} amenity${totalSelected === 1 ? "" : "ies"} selected`}
          </div>
        </div>

        <div className="space-y-[10px]">
          <div className="bg-white px-[12px] py-[8px] border-[1px] border-[#E3E3E3] rounded-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (e.target.value) setCurrentCategory("All");
              }}
              placeholder="Search amenities"
              className="outline-none w-full font-normal"
            />
          </div>
          <div className="flex flex-wrap gap-[10px]">
            {AMENITY_CATEGORIES.map((item) => (
              <div
                key={item}
                onClick={() => {
                  setCurrentCategory(item);
                  setSearch("");
                }}
                className={
                  currentCategory === item && !search
                    ? "bg-[#dddddd] hover:bg-[#dddddd] cursor-pointer select-none px-[14px] py-[8px] rounded-[12px] w-max border-[1px] border-[#E3E3E3]"
                    : "hover:bg-[#dddddd] cursor-pointer select-none px-[14px] py-[8px] rounded-[12px] w-max border-[1px] border-[#E3E3E3]"
                }
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-[10px] max-h-[360px] overflow-auto scrollbarwidth p-[10px] bg-[#fdfdfd] rounded-[10px]">
          {normalizedGroups.length === 0 ? (
            <div className="col-span-2 text-center text-[#999999] py-6">
              No amenities match “{search}”.
            </div>
          ) : (
            normalizedGroups.map((group) => {
              if (!shouldRenderGroup(group.key)) return null;

              return group.items.map((amenity) => {
                const selected = isSelected(group.key, amenity);
                return (
                  <div
                    key={`${group.key}-${amenity}`}
                    className="w-full flex items-center justify-between px-[10px] py-[8px] bg-white rounded-[12px] border border-[#E3E3E3]"
                  >
                    <div className="flex items-center gap-[10px]">
                      <AmenityIcons item={amenity} />
                    </div>
                    {selected ? (
                      <CheckMarkPurple
                        className="cursor-pointer"
                        onClick={() => toggleAmenity(group.key, amenity)}
                      />
                    ) : (
                      <PlusIcon
                        className="cursor-pointer"
                        onClick={() => toggleAmenity(group.key, amenity)}
                      />
                    )}
                  </div>
                );
              });
            })
          )}
        </div>
      </div>

      <div className="w-full h-[100px] px-[100px] flex justify-between items-center mt-4">
        <WhiteButton text="Back" onClick={onBack} />
        <PurpleButton text="Next" onClick={onNext} />
      </div>
    </div>
  );
});

MintAmenitiesPage.displayName = "MintAmenitiesPage";

export default MintAmenitiesPage;

